import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import * as api from "./client";

const PAGE_SIZE = 50;

// ── Withdrawals ────────────────────────────────────────────────

const withdrawalRoot = ["forex", "withdrawals"];

// A plain query, not an infinite one: the queue route takes no limit and no cursor
// and returns every SUBMITTED row at once. Same live-work-list settings as the
// verification queue.
export function useWithdrawalQueue() {
  return useQuery({
    queryKey: [...withdrawalRoot, "queue"],
    queryFn: api.fetchWithdrawalQueue,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

const useInvalidateQueue = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [...withdrawalRoot, "queue"] });
};

// onSettled, not onSuccess: a 409 means another admin already actioned this row, so
// the stale queue has to be dropped and refetched either way.
export function useMarkSent() {
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: (id) => api.markWithdrawalSent(id),
    onSettled: invalidate,
  });
}

export function useDeclineWithdrawal() {
  const invalidate = useInvalidateQueue();
  return useMutation({
    mutationFn: (id) => api.declineWithdrawal(id),
    onSettled: invalidate,
  });
}

// ── Deposits (admin) ───────────────────────────────────────────

const depositRoot = ["forex", "deposits"];

export function usePendingDeposits() {
  return useQuery({
    queryKey: [...depositRoot, "pending"],
    queryFn: api.fetchPendingDeposits,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

export function useNotCreditedDeposits() {
  return useQuery({
    queryKey: [...depositRoot, "not-credited"],
    queryFn: api.fetchNotCreditedDeposits,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

// Deliberately NOT refetched on focus: this route reads a live chain balance per
// row and has no pagination, so every call gets slower as funded addresses build
// up. An explicit Refresh only.
export function useUncollected() {
  return useQuery({
    queryKey: [...depositRoot, "uncollected"],
    queryFn: api.fetchUncollected,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
}

// The one admin route that pages. A page shorter than the limit is the end.
export function useCollections() {
  return useInfiniteQuery({
    queryKey: [...depositRoot, "collections"],
    queryFn: ({ pageParam }) => api.fetchCollections({ limit: PAGE_SIZE, cursor: pageParam }),
    initialPageParam: undefined,
    getNextPageParam: (last) => last?.nextCursor ?? undefined,
  });
}

const useInvalidateDeposits = (...keys) => {
  const queryClient = useQueryClient();
  return () =>
    keys.forEach((key) =>
      queryClient.invalidateQueries({ queryKey: [...depositRoot, key] }),
    );
};

// onSettled, not onSuccess: a 409 means another admin already actioned the row.
export function useCreditDeposit() {
  const invalidate = useInvalidateDeposits("pending");
  return useMutation({
    mutationFn: (id) => api.creditDeposit(id),
    onSettled: invalidate,
  });
}

export function useRefundDeposit() {
  const invalidate = useInvalidateDeposits("not-credited");
  return useMutation({
    mutationFn: (id) => api.refundDeposit(id),
    onSettled: invalidate,
  });
}

// A recorded sweep changes both lists: rows leave uncollected and land in history.
export function useRecordCollection() {
  const invalidate = useInvalidateDeposits("uncollected", "collections");
  return useMutation({
    mutationFn: (txHash) => api.recordCollection(txHash),
    onSettled: invalidate,
  });
}
